import { cloneDeep, get, has, isEqual } from "lodash-es";
import { fieldTokenPattern, parseFieldExpression } from "../fieldExpression";
import {
    evaluateFieldExpression,
    unsupportedPreview,
    type PreviewValue,
} from "../fieldModifiers";
import type { Locale } from "~/models/Localization";
import type { FieldOption } from "~/models/Automation";
import type { FieldDefinition } from "~/models/Automation";
interface ReplaceContentOptions {
    except?: string[];
    only?: string[];
    parent?: string | null;
    type?: FieldMapType;
    forValidation?: boolean;
    showOptionLabel?: boolean;
    nullOnEmpty?: boolean;
    labelAsValue?: boolean;
    replaceWhenEmpty?: boolean;
    skipEmptyValues?: boolean;
    prefix?: string;
    suffix?: string;
    fallback?: any;
}

export enum FieldMapType {
    DataKey,
    DataKeyAsValue,
    Default,
    Label,
    Display,
}

export type SimpleFieldDefinition<TMetadata = any> = Partial<
    FieldDefinition<any, TMetadata>
> &
    Pick<FieldDefinition<any, TMetadata>, "key" | "type" | "default_value">;

export type FieldLocale =
    | string
    | (Partial<Pick<Locale, "code">> & { id?: number | null })
    | null;

export class FieldCollection {
    public items: FieldDefinition[];
    private locale: FieldLocale = null;
    private mapType = FieldMapType.Default;
    private account: { locale: FieldLocale } | null = null;

    constructor(items: FieldDefinition[] = []) {
        this.items = cloneDeep(items) || [];
    }

    setMapType(type: FieldMapType) {
        this.mapType = type;
        return this;
    }

    setLocale(locale: FieldLocale) {
        this.locale = locale;
        return this;
    }

    setAccount(account: { locale: FieldLocale } | null) {
        this.account = account;
        return this;
    }

    getLocale() {
        return this.locale ?? this.account?.locale ?? null;
    }

    private withContext(items: FieldDefinition[]) {
        return new FieldCollection(items)
            .setAccount(this.account)
            .setLocale(this.locale)
            .setMapType(this.mapType);
    }

    private translateValue(translations: Record<string, unknown> = {}) {
        const normalize = (code: string) =>
            code.trim().toLowerCase().replaceAll("-", "_");
        const labels = Object.fromEntries(
            Object.entries(translations)
                .filter(
                    ([, value]) =>
                        typeof value === "string" && value.trim() !== "",
                )
                .map(([code, value]) => [normalize(code), value as string]),
        );

        const languages: string[] = [];
        for (const locale of [this.locale, this.account?.locale]) {
            const code = normalize(
                typeof locale === "string" ? locale : (locale?.code ?? ""),
            );
            if (
                !/^[a-z]{2,3}(?:_[a-z]{4})?(?:_[a-z]{2}|_[0-9]{3})?$/.test(code)
            )
                continue;
            const base = code.split("_")[0]!;
            languages.push(base);
            const exact =
                labels[code] ??
                (code.length === 2 ? labels[`${code}_${code}`] : undefined);
            const label = exact ?? labels[base];
            if (label !== undefined) return label;
        }
        const keys = Object.keys(labels).sort();
        for (const language of new Set(languages)) {
            const key = keys.find((key) => key.startsWith(`${language}_`));
            if (key !== undefined) return labels[key];
        }
        return undefined;
    }

    getOptionLabel(option: Pick<FieldOption, "label" | "translations">) {
        return this.translateValue(option.translations) ?? option.label;
    }

    getOptions(field: Pick<FieldDefinition, "options">) {
        return (field.options ?? []).map((option) => ({
            ...option,
            label: this.getOptionLabel(option),
        }));
    }

    getDisplayValue(field: FieldDefinition | string): unknown {
        const item =
            typeof field === "string" ? this.firstWhere("key", field) : field;
        if (!item) return null;
        const value =
            item.display_value ?? item.default_value ?? item.validation_value;
        const selected = item.validation_value ?? item.default_value;
        const display = (
            value: unknown,
            selected: unknown,
            translations: Record<string, unknown>,
            many = false,
        ): unknown => {
            if (Array.isArray(value)) {
                const values = value.map((entry, index) =>
                    display(
                        entry,
                        Array.isArray(selected) ? selected[index] : undefined,
                        Object.fromEntries(
                            Object.entries(translations).map(
                                ([locale, label]) => [
                                    locale,
                                    label && typeof label === "object"
                                        ? (label as Record<number, unknown>)[
                                              index
                                          ]
                                        : undefined,
                                ],
                            ),
                        ),
                    ),
                );
                return !many &&
                    !value.some(Array.isArray) &&
                    item.type === "list" &&
                    values.every(
                        (entry) =>
                            entry === null ||
                            ["string", "number", "boolean"].includes(
                                typeof entry,
                            ),
                    )
                    ? values
                          .map((entry) =>
                              entry === false
                                  ? ""
                                  : entry === true
                                    ? "1"
                                    : entry,
                          )
                          .join(", ")
                    : values;
            }
            const option = item.options?.find(
                (option) =>
                    option.value === selected ||
                    (["string", "number"].includes(typeof option.value) &&
                        ["string", "number"].includes(typeof selected) &&
                        String(option.value) === String(selected)),
            );
            return (
                this.translateValue({
                    ...option?.translations,
                    ...translations,
                }) ??
                (item.display_value != null ? value : (option?.label ?? value))
            );
        };
        return display(
            value,
            selected,
            item.translations ?? {},
            item.metadata?.data_reference?.cardinality === "many",
        );
    }

    static match(text: string, prefix: string, suffix: string) {
        return Array.from(
            text.matchAll(new RegExp(fieldTokenPattern(prefix, suffix), "g")),
        );
    }

    getKeyValueMap(
        options?: Partial<{
            dataKey: boolean;
            dataKeyAsValue: boolean;
            asOptionLabel: boolean;
            forValidation: boolean;
            label: boolean;
            display: boolean;
        }>,
    ) {
        const {
            dataKey = false,
            dataKeyAsValue = false,
            asOptionLabel = false,
            forValidation = false,
            label = false,
            display = false,
        } = options ?? {};
        const map: Record<string, any> = {};

        for (const item of this.items) {
            const key = dataKey ? item.data_key : item.key;

            let value: any;

            if (dataKeyAsValue) {
                value = item.data_key;
            } else if (forValidation) {
                value = item.validation_value ?? item.default_value;
            } else if (label) {
                value = item.label;
            } else if (display || asOptionLabel) {
                value = this.getDisplayValue(item);
            } else {
                value = item.default_value;
            }

            if (key in map) {
                map[key] = map[key] || value;
            } else {
                map[key] = value;
            }

            if (!dataKey && item.aliases) {
                for (const alias of item.aliases) {
                    map[alias] = value;
                }
            }
        }

        return map;
    }

    findMatches(
        text: string | string[],
        options: Partial<{
            type: FieldMapType;
            prefix: string;
            suffix: string;
        }> = {},
    ) {
        if (typeof text === "string") text = [text];
        options = {
            type: this.mapType,
            prefix: "{{",
            suffix: "}}",
            ...options,
        };

        const matches = [...text];

        text.forEach((txt) => {
            const match = FieldCollection.match(
                txt,
                options.prefix as string,
                options.suffix as string,
            ).map((match) => match[1] ?? "");

            matches.push(...match);
        });

        return this.withContext(
            useArrayUnique(matches)
                .value.map((expression) => {
                    const value = parseFieldExpression(expression)?.key;
                    if (FieldMapType.DataKey === options.type) {
                        return this.firstWhere("data_key", value);
                    } else {
                        return this.firstWhere(
                            (field) =>
                                field.key === value ||
                                (field.aliases ?? []).includes(value as string),
                        );
                    }
                })
                .filter((value) => !!value) as FieldDefinition[],
        );
    }

    repeatReplaceContent(
        content: any,
        count: number,
        options: ReplaceContentOptions = {},
    ) {
        for (let i = 0; i < count; i++) {
            content = this.replaceContent(content, options);
        }

        return content;
    }

    replaceContent(text: any, options: ReplaceContentOptions = {}) {
        if (!text) {
            return text;
        }

        const initialText = text;

        options = {
            except: [],
            only: [],
            parent: null,
            type: this.mapType,
            forValidation: false,
            nullOnEmpty: false,
            showOptionLabel: false,
            replaceWhenEmpty: true,
            prefix: "{{",
            suffix: "}}",
            ...options,
        };

        if (Array.isArray(text) || typeof text === "object") {
            for (const key in text) {
                const parentKey = options.parent
                    ? `${options.parent}.${key}`
                    : key;

                if (
                    options.except?.includes(parentKey) ||
                    (options?.only?.length &&
                        !options.only?.includes(parentKey))
                ) {
                    continue;
                }

                if (
                    typeof text[key] === "string" ||
                    Array.isArray(text[key]) ||
                    typeof text[key] === "object"
                ) {
                    text[key] = this.replaceContent(text[key], {
                        ...options,
                        parent: parentKey,
                    });
                }
            }
        }

        if (typeof text !== "string") {
            return text;
        }

        const tokens = [
            text,
            ...FieldCollection.match(
                text,
                options.prefix as string,
                options.suffix as string,
            ).map((match) => match[1] ?? ""),
        ];
        const values = tokens
            .map((expression) =>
                this.findExpressionSubject(expression, options),
            )
            .filter((field): field is FieldDefinition => !!field);

        const map = this.withContext(values);

        let valueMap;

        if (options.showOptionLabel || options.type === FieldMapType.Display) {
            valueMap = map.getKeyValueMap({
                display: true,
                forValidation: options.forValidation,
            });
        } else if (options.type === FieldMapType.DataKey) {
            valueMap = map.getKeyValueMap({
                dataKey: true,
                forValidation: options.forValidation,
            });
        } else if (options.type === FieldMapType.Label) {
            valueMap = map.getKeyValueMap({
                label: true,
                forValidation: options.forValidation,
            });
        } else if (options.type === FieldMapType.DataKeyAsValue) {
            valueMap = map.getKeyValueMap({
                dataKeyAsValue: true,
                forValidation: options.forValidation,
            });
        } else {
            valueMap = map.getKeyValueMap({
                forValidation: options.forValidation,
            });
        }

        for (const token of tokens) {
            const parsed = parseFieldExpression(token);
            if (!parsed?.modifiers.length) continue;
            delete valueMap[token];
            delete valueMap[token.trim()];
            const result = evaluateFieldExpression(token, (key) =>
                this.previewValue(key, options, valueMap),
            );
            if (result.found && result.value !== unsupportedPreview)
                valueMap[token] = result.value;
        }

        if (text in valueMap) {
            return valueMap[text] ?? "";
        }

        text = text.replace(
            new RegExp(fieldTokenPattern(options.prefix, options.suffix), "g"),
            (token: string, expression: string) => {
                const key = Object.hasOwn(valueMap, expression)
                    ? expression
                    : expression.trim();
                if (!Object.hasOwn(valueMap, key)) return token;
                const value = valueMap[key];
                if (options.skipEmptyValues && isBlankValue(value))
                    return token;
                if (!options.replaceWhenEmpty && !value) return token;
                if (value !== null && typeof value === "object")
                    return JSON.stringify(value);
                return value == null ? "" : String(value);
            },
        );

        if (!text && options.fallback) {
            return options.fallback;
        }

        return text === initialText && options.nullOnEmpty ? null : text.trim();
    }

    private findExpressionSubject(
        expression: string,
        options: ReplaceContentOptions,
    ) {
        const key = parseFieldExpression(expression)?.key;
        if (options.type === FieldMapType.DataKey)
            return this.firstWhere("data_key", key);
        return this.firstWhere(
            (field) =>
                field.key === key || (field.aliases ?? []).includes(key ?? ""),
        );
    }

    private previewValue(
        key: string,
        options: ReplaceContentOptions,
        map: Record<string, unknown>,
    ): PreviewValue {
        const field = this.findExpressionSubject(key, options);
        if (!field) return { value: null, found: false };
        const value = Object.hasOwn(map, key)
            ? map[key]
            : options.forValidation
              ? (field.validation_value ?? field.default_value)
              : options.showOptionLabel || options.type === FieldMapType.Display
                ? this.getDisplayValue(field)
                : field.default_value;
        return { value, type: field.type, found: true };
    }

    firstWhere(
        key: keyof FieldDefinition | ((item: FieldDefinition) => boolean),
        value?: any,
    ) {
        return this.items.find((item) =>
            typeof key === "function" ? key(item) : item[key] === value,
        );
    }

    where(
        key: keyof FieldDefinition | ((item: FieldDefinition) => boolean),
        value?: any,
    ) {
        return this.items.filter((item) =>
            typeof key === "function" ? key(item) : item[key] === value,
        );
    }

    getExternalId() {
        return this.firstWhere("is_external_id", true);
    }

    withData(
        data: Record<string, any> = {},
        options: Partial<{
            ignoreEmpty: boolean;
            transform: (field: FieldDefinition) => FieldDefinition;
            wipe: boolean;
        }> = {},
    ) {
        const { ignoreEmpty = false, transform, wipe = false } = options;

        if ((!data || Object.keys(data).length === 0) && wipe) {
            this.items = (this.items || []).map((field) => {
                field.default_value = null;
                field.validation_value = null;
                field.display_value = null;
                field.translations = undefined;
                return field;
            });
            return this;
        }

        this.items = (this.items || []).map((field) => {
            if (wipe) {
                field.default_value = null;
                field.validation_value = null;
                field.display_value = null;
                field.translations = undefined;
            }

            // Prefer data_key, fallback to key
            const dataKey = field?.data_key ?? field?.key;
            let value: any = undefined;
            let hasMatchingValue = false;

            if (dataKey in data && (!ignoreEmpty || data[dataKey])) {
                value = data[dataKey];
                hasMatchingValue = true;
            } else if (has(data, dataKey)) {
                value = get(data, dataKey);
                hasMatchingValue = true;
            } else {
                // Check aliases
                for (const alias of field.aliases ?? []) {
                    if (alias in data && (!ignoreEmpty || data[alias])) {
                        value = data[alias];
                        hasMatchingValue = true;
                        break;
                    } else if (has(data, alias)) {
                        value = get(data, alias);
                        hasMatchingValue = true;
                        break;
                    }
                }
            }

            const isValueEmpty =
                value === undefined || value === null || value === "";
            const isValueIgnored = ignoreEmpty && isValueEmpty;

            if (
                hasMatchingValue &&
                !isValueIgnored &&
                !isEqual(field.default_value, value)
            ) {
                field.default_value = value;
                field.validation_value = null;
                field.display_value = null;
                field.translations = undefined;
            }

            return transform ? transform(field) : field;
        });

        return this;
    }

    all(): FieldDefinition[] {
        return this.items;
    }

    add(...field: FieldDefinition[]) {
        field.forEach((item) => {
            if (!item.label) {
                item.label = item.key;
            }

            if (!item.data_key) {
                item.data_key = item.key;
            }

            if (!item.attribute) {
                item.attribute = item.key;
            }

            const index = this.items.findIndex(
                (existing) => existing.key === item.key,
            );
            if (index >= 0) {
                this.items.splice(index, 1, item);
            } else {
                this.items.push(item);
            }
        });

        return this;
    }

    addSimple<TMetadata = Record<string, unknown> | null>(
        ...field: SimpleFieldDefinition[]
    ) {
        return this.add(...(field as FieldDefinition<any, TMetadata>[]));
    }
}

export function createFieldCollection(
    fields: FieldDefinition[] | FieldCollection = [],
) {
    return fields instanceof FieldCollection
        ? fields
        : new FieldCollection(fields);
}

export function createSimpleFieldCollection(fields: SimpleFieldDefinition[]) {
    return createFieldCollection().addSimple(...fields);
}
